import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import bcrypt from "bcryptjs";
import { routes } from "../routes";

export default function ResetPassword({ onNavigate, onLoggedIn, onNotify }) {
  const [status, setStatus] = useState("checking"); // checking | ready | invalid | used | expired | done
  const [tokenRow, setTokenRow] = useState(null);   // { cliente_id, ... }
  const [pass1, setPass1] = useState("");
  const [pass2, setPass2] = useState("");
  const [loading, setLoading] = useState(false);

  // Leer token de la URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token") || "";
    if (!token) {
      setStatus("invalid");
      return;
    }
    (async () => {
      try {
        const { data: row } = await supabase
          .from("password_reset_tokens")
          .select("id, cliente_id, expires_at, used_at, created_at")
          .eq("token", token)
          .maybeSingle();

        if (!row) { setStatus("invalid"); return; }
        if (row.used_at) { setStatus("used"); return; }
        if (new Date(row.expires_at) < new Date()) { setStatus("expired"); return; }

        setTokenRow(row);
        setStatus("ready");
      } catch (e) {
        console.error(e);
        setStatus("invalid");
      }
    })();
  }, []);

  const handleReset = async (e) => {
    e.preventDefault();
    if (!pass1 || pass1.length < 6) {
      (onNotify || (()=>{}))({ variant:"warning", title:"Contraseña muy corta", description:"Debe tener al menos 6 caracteres." });
      return;
    }
    if (pass1 !== pass2) {
      (onNotify || (()=>{}))({ variant:"warning", title:"No coinciden", description:"Verifica la confirmación." });
      return;
    }
    if (!tokenRow?.cliente_id) return;

    try {
      setLoading(true);
      // 1) Hashear y actualizar contraseña
      const hash = await bcrypt.hash(pass1, 10);
      const { error: upErr } = await supabase
        .from("clientes")
        .update({ contrasena_hash: hash })
        .eq("id", tokenRow.cliente_id);
      if (upErr) throw upErr;

      // 2) Marcar token como usado
      await supabase
        .from("password_reset_tokens")
        .update({ used_at: new Date().toISOString() })
        .eq("id", tokenRow.id);

      setStatus("done");

      // 3) (Opcional) Iniciar sesión automática
      const { data: cli } = await supabase
        .from("clientes")
        .select("id, nombre, correo")
        .eq("id", tokenRow.cliente_id)
        .maybeSingle();

      if (cli?.id) {
        onLoggedIn?.({ id: cli.id, nombre: cli.nombre, correo: cli.correo });
        const first = (cli.nombre || "").trim().split(/\s+/)[0] || "¡Hola!";
        (onNotify || (()=>{}))({
          variant:"success",
          title:`¡Listo, ${first}!`,
          description:"Tu contraseña fue actualizada.",
        });
        onNavigate?.(routes.home);
      } else {
        (onNotify || (()=>{}))({
          variant:"success",
          title:"Contraseña actualizada",
          description:"Inicia sesión nuevamente con tu nueva contraseña.",
        });
        onNavigate?.(routes.login);
      }
    } catch (err) {
      console.error(err);
      (onNotify || (()=>{}))({
        variant:"error",
        title:"No se pudo actualizar",
        description:"Intenta nuevamente en unos minutos.",
      });
    } finally {
      setLoading(false);
    }
  };

  const renderBody = () => {
    if (status === "checking") return <p className="text-gray-600">Validando enlace…</p>;
    if (status === "invalid")  return <p className="text-red-600">Enlace inválido. Solicita uno nuevo.</p>;
    if (status === "used")     return <p className="text-red-600">Este enlace ya fue usado. Solicita uno nuevo.</p>;
    if (status === "expired")  return <p className="text-red-600">El enlace expiró. Solicita uno nuevo.</p>;
    if (status === "done")     return <p className="text-green-600">Actualizada. Redirigiendo…</p>;

    // ready
    return (
      <form onSubmit={handleReset} className="space-y-4">
        <div>
          <label className="block text-sm text-gray-700 mb-1">Nueva contraseña</label>
          <input
            type="password"
            className="w-full rounded-lg border-gray-300 focus:border-brand focus:ring-brand px-3 py-2"
            value={pass1}
            onChange={(e)=>setPass1(e.target.value)}
            placeholder="••••••••"
            required
            minLength={6}
          />
        </div>
        <div>
          <label className="block text-sm text-gray-700 mb-1">Confirmar contraseña</label>
          <input
            type="password"
            className="w-full rounded-lg border-gray-300 focus:border-brand focus:ring-brand px-3 py-2"
            value={pass2}
            onChange={(e)=>setPass2(e.target.value)}
            placeholder="••••••••"
            required
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-brand text-white py-3 rounded-lg font-semibold hover:bg-brand/90 transition-colors disabled:opacity-70"
        >
          {loading ? "Actualizando…" : "Guardar nueva contraseña"}
        </button>
      </form>
    );
  };

  return (
    <section className="min-h-[70vh] px-4 sm:px-6 lg:px-8 flex items-center justify-center">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Crear nueva contraseña</h1>
        {renderBody()}
      </div>
    </section>
  );
}
